import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PublicHome from "@/pages/PublicHome";
import PublicJobs from "@/pages/PublicJobs";
import PublicJobDetail from "@/pages/PublicJobDetail";
import Dashboard from "@/pages/Dashboard";
import Companies from "@/pages/Companies";
import Candidates from "@/pages/Candidates";
import JobOrders from "@/pages/JobOrders";
import Recruiters from "@/pages/Recruiters";
import Pipeline from "@/pages/Pipeline";
import AIMatching from "@/pages/AIMatching";
import AccountSettings from "@/pages/AccountSettings";
import CountryAdmin from "@/pages/CountryAdmin";
import PortalSignup from "@/pages/PortalSignup";
import CandidatePortal from "@/pages/CandidatePortal";
import CandidateResume from "@/pages/CandidateResume";
import EmployerSignup from "@/pages/EmployerSignup";
import EmployerStatus from "@/pages/EmployerStatus";
import EmployerDashboard from "@/pages/EmployerDashboard";
import SmartJobBuilder from "@/pages/SmartJobBuilder";
import EmployerMatches from "@/pages/EmployerMatches";
import EmployerInterviews from "@/pages/EmployerInterviews";
import EmployerOffers from "@/pages/EmployerOffers";
import EmployerPlacements from "@/pages/EmployerPlacements";
import CandidateOffers from "@/pages/CandidateOffers";
import WorkforceAssessment from "@/pages/WorkforceAssessment";
import AdminEmployers from "@/pages/AdminEmployers";
import AdminCommercial from "@/pages/AdminCommercial";
import AdminCompliance from "@/pages/AdminCompliance";
import CandidateRecord from "@/pages/CandidateRecord";
import EmployerCandidate from "@/pages/EmployerCandidate";
import MediaReview from "@/pages/MediaReview";
import Shortlists from "@/pages/Shortlists";
import Interviews from "@/pages/Interviews";
import Placements from "@/pages/Placements";
import Qualification from "@/pages/Qualification";
import SourcingRequests from "@/pages/SourcingRequests";
import PublicPricing from "@/pages/PublicPricing";
import Advertise from "@/pages/Advertise";
import Workforce from "@/pages/Workforce";
import WorkforceSolution from "@/pages/WorkforceSolution";
import WorkforceDepartment from "@/pages/WorkforceDepartment";
import ComingSoon from "@/pages/ComingSoon";

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Redirect candidates to their portal
  if (user.role === "candidate" && role !== "candidate") return <Navigate to="/portal" replace />;
  if (role === "candidate" && user.role !== "candidate") return <Navigate to="/dashboard" replace />;
  if (user.role === "client" && role !== "client") return <Navigate to="/employer/dashboard" replace />;
  if (role === "client" && user.role !== "client") return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/pricing" element={<PublicPricing />} />
          <Route path="/post-a-job" element={<PublicPricing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/portal/signup" element={<PortalSignup />} />
          <Route path="/portal" element={<Protected role="candidate"><CandidatePortal /></Protected>} />
          <Route path="/candidate-portal/resume" element={<Protected role="candidate"><CandidateResume /></Protected>} />
          <Route path="/employer-portal/signup" element={<EmployerSignup />} />
          <Route path="/employer-portal/status" element={<Protected role="client"><EmployerStatus /></Protected>} />
          <Route path="/employer/dashboard" element={<Protected role="client"><EmployerDashboard /></Protected>} />
          <Route path="/employer/job-builder" element={<Protected role="client"><SmartJobBuilder /></Protected>} />
          <Route path="/employer/matches" element={<Protected role="client"><EmployerMatches /></Protected>} />
          <Route path="/employer/interviews" element={<Protected role="client"><EmployerInterviews /></Protected>} />
          <Route path="/employer/offers" element={<Protected role="client"><EmployerOffers /></Protected>} />
          <Route path="/employer/placements" element={<Protected role="client"><EmployerPlacements /></Protected>} />
          <Route path="/portal/offers" element={<Protected role="candidate"><CandidateOffers /></Protected>} />
          <Route path="/employer/workforce-assessment" element={<Protected role="client"><WorkforceAssessment /></Protected>} />
          <Route path="/admin/employers" element={<Protected><AdminEmployers /></Protected>} />
          <Route path="/admin/commercial" element={<Protected><AdminCommercial /></Protected>} />
          <Route path="/admin/compliance" element={<Protected><AdminCompliance /></Protected>} />
          <Route path="/" element={<PublicHome />} />
          <Route path="/jobs" element={<PublicJobs />} />
          <Route path="/jobs/:id" element={<PublicJobDetail />} />
          <Route path="/advertise" element={<Advertise />} />
          <Route path="/workforce" element={<Workforce />} />
          <Route path="/workforce/departments/:slug" element={<WorkforceDepartment />} />
          <Route path="/workforce/:slug" element={<WorkforceSolution />} />
          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/companies" element={<Protected><Companies /></Protected>} />
          <Route path="/candidates" element={<Protected><Candidates /></Protected>} />
          <Route path="/candidates/:id" element={<Protected><CandidateRecord /></Protected>} />
          <Route path="/employer/candidates/:id" element={<Protected><EmployerCandidate /></Protected>} />
          <Route path="/media-review" element={<Protected><MediaReview /></Protected>} />
          <Route path="/job-orders" element={<Protected><JobOrders /></Protected>} />
          <Route path="/recruiters" element={<Protected><Recruiters /></Protected>} />
          <Route path="/pipeline" element={<Protected><Pipeline /></Protected>} />
          <Route path="/matching" element={<Protected><AIMatching /></Protected>} />
          <Route path="/qualification" element={<Protected><Qualification /></Protected>} />
          <Route path="/shortlists" element={<Protected><Shortlists /></Protected>} />
          <Route path="/interviews" element={<Protected><Interviews /></Protected>} />
          <Route path="/placements" element={<Protected><Placements /></Protected>} />
          <Route path="/sourcing-requests" element={<Protected><SourcingRequests /></Protected>} />
          <Route path="/teams" element={<Protected><ComingSoon section="ConnectMyHub Teams" title="Managed departments" /></Protected>} />
          <Route path="/workforce" element={<Protected><ComingSoon section="Workforce" title="Managed, AI & Hybrid workforce" /></Protected>} />
          <Route path="/opportunities" element={<Protected><ComingSoon section="Opportunities" title="Opportunity Diagnosis" /></Protected>} />
          <Route path="/clients" element={<Protected><ComingSoon section="Clients" title="Client portal directory" /></Protected>} />
          <Route path="/memberships" element={<Protected><ComingSoon section="Memberships" title="Membership plans" /></Protected>} />
          <Route path="/academy" element={<Protected><ComingSoon section="ConnectMyHub Academy" title="Training & resource library" /></Protected>} />
          <Route path="/check-ins" element={<Protected><ComingSoon section="Check-Ins" title="Check-Ins are managed inside each Placement." /></Protected>} />
          <Route path="/tasks" element={<Protected><ComingSoon section="Tasks" title="Task & action center" /></Protected>} />
          <Route path="/reports" element={<Protected><ComingSoon section="Reports" title="Reports & ROI analytics" /></Protected>} />
          <Route path="/integrations" element={<Protected><ComingSoon section="Integrations" title="LeadHub, AIA, 1Hub CRM & more" /></Protected>} />
          <Route path="/countries-admin" element={<Protected><CountryAdmin /></Protected>} />
          <Route path="/account" element={<Protected><AccountSettings /></Protected>} />
          <Route path="/settings" element={<Protected><ComingSoon section="Settings" title="Platform settings" /></Protected>} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
